<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, isRef, type Ref, onBeforeUnmount, watch } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useLanguageStore } from '@/stores/languageStore'
import { getVideoId } from '../utils/youtube'
import apiService from '../services/apiService'
import pollingService from '@/services/pollingService'
import { FormatService } from '@/services/formatService'
import type { AuthUser } from '@/types/auth'
import type { TranscriptItem, VideoData } from '@/types/video'
import { VideoProcessingService } from '@/services/videoProcessingService'
import { useVideoStore } from '../stores/videoStore'
import Spinner from '../components/Spinner.vue'
import { useUIStore } from '../stores/uiStore'
import FeatureSection from '../components/home/FeatureSection.vue'
import PopularVideos from '../components/home/PopularVideos.vue'
import TestimonialSection from '../components/home/TestimonialSection.vue'
import HowItWorksSection from '../components/home/HowItWorksSection.vue'
import DetailModal from '../components/modals/DetailModal.vue'
import TranscriptModal from '../components/modals/TranscriptModal.vue'
import LanguageModal from '../components/modals/LanguageModal.vue'
import { debounce } from 'lodash-es'
import { supabase } from '@/config/supabase'
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'
// Ana sayfa için sadece yeni direkt sistem kullan
import {
  handleSearch as newHandleSearch,
  loadAvailableSummaries as newLoadAvailableSummaries
} from '@/services/newVideoProcessingService'
import NewVideoProcessingService from '@/services/newVideoProcessingService'
import { notifyError, notifyWarning, forceCloseSpinners, setSummaryTimeout, clearSummaryTimeouts } from '@/utils/uiStateManager'

interface VideoSummary {
  id: string;
  channelName: string;
  channelAvatar: string;
  videoTitle: string;
  videoThumbnail: string;
  summary: string;
  publishedAt: string;
  videoUrl: string;
  isRead: boolean;
  language: string;
}

interface RecentSummary {
  id: string;
  video_id: string;
  content: string;
  status: string;
  created_at: string;
  language: string;
  tts_content?: string;
  tts_url?: string;
  videos: {
    title: string;
    thumbnail_url: string;
    channel_title: string;
    published_at?: string;
  };
}

const languageStore = useLanguageStore()
const authStore = useAuthStore()
const videoStore = useVideoStore()
const uiStore = useUIStore()

const user = computed(() => authStore.user as AuthUser | null)
const currentLanguage = computed(() => languageStore.currentLocale)

const searchQuery = ref('')
const videoId = computed(() => videoStore.videoId)  // videoId'yi computed olarak tanımla
const isLoading = computed(() => videoStore.getLoadingState('video'))
const isLoadingSummary = computed(() => videoStore.getLoadingState('summary'))
const isLoadingTranscript = computed(() => videoStore.getLoadingState('transcript'))
const shouldShowSpinner = computed(() => uiStore.shouldShowSpinner)
const error = ref('')
const activeTab = ref('summary')
const isMenuOpen = ref(false)
const showDetailModal = ref(false)
const showLanguageModal = ref(false)
const selectedLanguage = ref('')
const pendingVideoUrl = ref('')
const showTranscriptModal = ref(false)
const selectedSummary = ref<VideoSummary | null>(null)
const summaries = ref<VideoSummary[]>([])
const forceRender = ref(0)
const recentSummaries = ref<RecentSummary[]>([])
const isLoadingRecentSummaries = ref(false)
const audioUrl = ref('') // Ses dosyası URL'i
const isLoadingAudio = ref(false) // Ses yükleme durumu

// SEO meta tags
const publicBase = (import.meta as any).env?.VITE_PUBLIC_BASE_URL || window.location.origin;

function setMeta(title: string, description: string) {
  document.title = title;
  const metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (metaDesc) {
    metaDesc.setAttribute('content', description);
  } else {
    const m = document.createElement('meta');
    m.setAttribute('name', 'description');
    m.setAttribute('content', description);
    document.head.appendChild(m);
  }
}

function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  const existing = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  const tag = existing || document.createElement('meta');
  tag.setAttribute(attr, key);
  tag.setAttribute('content', content);
  if (!existing) document.head.appendChild(tag);
}

function updateHomePageSEO() {
  const title = currentLanguage.value === 'tr' 
    ? 'Veciz AI - YouTube Video Özetleme ve AI Destekli İçerik Analizi'
    : 'Veciz AI - YouTube Video Summarization and AI-Powered Content Analysis';
  
  const description = currentLanguage.value === 'tr'
    ? 'YouTube videolarını AI ile özetleyin, transkript oluşturun ve sesli özetler dinleyin. Hızlı, doğru ve kullanıcı dostu video analizi.'
    : 'Summarize YouTube videos with AI, create transcripts and listen to audio summaries. Fast, accurate and user-friendly video analysis.';

  setMeta(title, description);
  
  // Open Graph
  setMetaTag('property', 'og:title', title);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'og:type', 'website');
  setMetaTag('property', 'og:url', publicBase);
  setMetaTag('property', 'og:image', `${publicBase}/veciz-logo.png`);
  setMetaTag('property', 'og:site_name', 'Veciz AI');
  
  // Twitter
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', title);
  setMetaTag('name', 'twitter:description', description);
  setMetaTag('name', 'twitter:image', `${publicBase}/veciz-logo.png`);
  
  // Canonical
  const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (canonical) {
    canonical.setAttribute('href', publicBase);
  } else {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', publicBase);
    document.head.appendChild(link);
  }
  
  // Add structured data for homepage
  addHomePageStructuredData();
}

function addHomePageStructuredData() {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Veciz AI",
    "url": publicBase,
    "description": currentLanguage.value === 'tr' 
      ? "YouTube videolarını AI ile özetleyin, transkript oluşturun ve sesli özetler dinleyin."
      : "Summarize YouTube videos with AI, create transcripts and listen to audio summaries.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${publicBase}/?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Veciz AI",
      "url": publicBase
    }
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
}

// Audio player referansları
const ttsAudio = ref<HTMLAudioElement | null>(null);
const backgroundAudio = ref<HTMLAudioElement | null>(null);
const isPlaying = ref(false);
const audioProgress = ref(0);
const currentTime = ref('0:00');
const totalDuration = ref('0:00');

// Ses seviyeleri
const ttsVolume = ref(1.0);
const bgVolume = ref(0.01);  // 0.02'den 0.01'e düşürüldü (1%)

// Süreyi formatla (dakika:saniye)
const formatTime = (timeInSeconds: number): string => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Audio ilerleme takibi
const updateAudioProgress = () => {
  if (ttsAudio.value) {
    audioProgress.value = (ttsAudio.value.currentTime / ttsAudio.value.duration) * 100;
    currentTime.value = formatTime(ttsAudio.value.currentTime);
    totalDuration.value = formatTime(ttsAudio.value.duration);
  }
};

// Ses seviyesi değişikliklerini izle
watch(ttsVolume, (newVolume) => {
  if (ttsAudio.value) {
    ttsAudio.value.volume = newVolume;
  }
});

watch(bgVolume, (newVolume) => {
  if (backgroundAudio.value) {
    backgroundAudio.value.volume = newVolume;
  }
});

// activeTab değiştiğinde Listen tabından çıkılırsa sesleri durdur
watch(activeTab, (newTab, oldTab) => {
  if (oldTab === 'listen' && newTab !== 'listen') {
    // Listen tabından çıkıldığında sesleri durdur
    resetAudio();
  }
});

// videoId değiştiğinde TTS verilerini temizle ve gerekirse yeni ses dosyası oluştur
watch(() => videoId.value, (newVideoId, oldVideoId) => {
  console.log(`🔄 Video ID değişti: ${oldVideoId} -> ${newVideoId}`);
  
  // Yeni video yüklendiğinde TTS verilerini tamamen temizle
  if (newVideoId !== oldVideoId && oldVideoId) {
    console.log('🧹 Yeni video yükleniyor, TTS verileri temizleniyor...');
    
    // Audio elementlerini temizle
    if (ttsAudio.value) {
      ttsAudio.value.pause();
      ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
      ttsAudio.value.removeEventListener('ended', handleTTSEnded);
      ttsAudio.value.src = '';
      ttsAudio.value.load();
      ttsAudio.value = null;
    }
    
    if (backgroundAudio.value) {
      backgroundAudio.value.pause();
      backgroundAudio.value.currentTime = 0;
    }
    
    // Audio URL'i temizle
    if (audioUrl.value) {
      try {
        URL.revokeObjectURL(audioUrl.value);
        console.log('🗑️ Eski audio URL temizlendi:', audioUrl.value);
      } catch (err) {
        console.error('Audio URL temizlenirken hata:', err);
      }
      audioUrl.value = '';
    }
    
    // Audio durumlarını sıfırla
    isPlaying.value = false;
    audioProgress.value = 0;
    currentTime.value = '0:00';
    totalDuration.value = '0:00';
    isLoadingAudio.value = false;
    
    console.log('✅ TTS verileri başarıyla temizlendi');
  }
  
  // Eğer listen tab aktifse ve yeni video için summary varsa yeni ses dosyası oluştur
  if (activeTab.value === 'listen' && videoData.value?.summary && !isLoadingAudio.value && newVideoId) {
    console.log('🎵 Listen tabı aktif, yeni video için ses dosyası oluşturuluyor...');
    setTimeout(() => {
      textToSpeech();
    }, 500); // Biraz daha uzun bekle ki video verileri yüklensin
  }
});

const videoData = computed(() => videoStore.videoData);

// Add computed property to use authStore
const isAuthenticated = computed(() => !!authStore.user)

// Add status tracking
const transcriptionStatus = ref({
  transcribing: false,
  detectingLanguage: false,
  savingTranscript: false,
  generatingSummary: false,
  message: ''
})

// State için yeni ref'ler ekle
const processingStatus = ref({
  isProcessing: false,
  currentStep: '',
  steps: {
    FETCHING: languageStore.t('home.processing.fetchingInfo'),
    TRANSCRIBING: languageStore.t('home.processing.creatingTranscript'),
    SUMMARIZING: languageStore.t('home.processing.creatingSummary'),
    SAVING: languageStore.t('home.processing.savingResults')
  }
});

const updateProcessingStatus = (step: keyof typeof processingStatus.value.steps) => {
  processingStatus.value.isProcessing = true;
  processingStatus.value.currentStep = processingStatus.value.steps[step];
};

// Close menu when clicking outside
const closeMenu = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  const profileButton = document.querySelector('.profile-button')
  const dropdownMenu = document.querySelector('.dropdown-menu')
  
  if (!profileButton?.contains(target) && !dropdownMenu?.contains(target)) {
    isMenuOpen.value = false
  }
}

// Create video processing service instance
const videoProcessingService = new VideoProcessingService(
  videoData,
  error,
  processingStatus
);

// Script section: add a ref for DEV mode
const isDev = ref(import.meta.env.DEV);

// Değişken temizleme fonksiyonunu saklamak için 
let cleanupLanguageListener: (() => void) | null = null;

// First, add the showDebug ref that's missing
const showDebug = ref(false);

// Format date for video publishing display
const formatPublishedDate = (dateString: string | undefined): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return 'Bugün';
  } else if (diffInDays === 1) {
    return 'Dün';
  } else if (diffInDays < 7) {
    return `${diffInDays} gün önce`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} hafta önce`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `${months} ay önce`;
  } else {
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  }
};

// Add function to fetch recent summaries
const fetchRecentSummaries = async () => {
  try {
    isLoadingRecentSummaries.value = true;
    
    // Herkesin görebilmesi için kimlik doğrulama kontrolünü kaldırıyoruz
    console.log('Fetching recent summaries...');
    
    let response;
    
    // Kullanıcı giriş yapmışsa token ile istek at, yoksa token olmadan
    if (isAuthenticated.value) {
      // Get session token
      const token = await supabase.auth.getSession();
      if (token.data.session?.access_token) {
        response = await fetch(`${import.meta.env.VITE_API_URL}/api/summaries/recent`, {
          headers: {
            'Authorization': `Bearer ${token.data.session.access_token}`
          }
        });
      } else {
        // Token yoksa token olmadan istek yap
        response = await fetch(`${import.meta.env.VITE_API_URL}/api/summaries/recent`);
      }
    } else {
      // Kullanıcı giriş yapmamışsa, token olmadan istek yap
      response = await fetch(`${import.meta.env.VITE_API_URL}/api/summaries/recent`);
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch recent summaries: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Recent summaries API response:', {
      status: response.status,
      data: data
    });
    
    // Handle both formats - either direct array or {data: [...]}
    if (Array.isArray(data)) {
      // Direct array response
      recentSummaries.value = data;
      console.log(`✅ Successfully loaded ${recentSummaries.value.length} recent summaries (direct array)`);
    } else if (data && Array.isArray(data.data)) {
      // Object with data property containing array
      recentSummaries.value = data.data;
      console.log(`✅ Successfully loaded ${recentSummaries.value.length} recent summaries (from data property)`);
    } else {
      console.error('Received invalid data format:', data);
      error.value = 'Failed to load recent summaries: Invalid data format';
      // Provide a fallback to prevent breaking the UI
      recentSummaries.value = [];
    }
  } catch (err) {
    console.error('Error fetching recent summaries:', err);
    error.value = err instanceof Error ? err.message : 'Failed to load recent summaries';
  } finally {
    isLoadingRecentSummaries.value = false;
  }
};

// Add function to handle thumbnail click
const handleThumbnailClick = async (videoId: string, language: string) => {
  try {
    console.log('🖼️ Thumbnail tıklandı - Direkt veri çekiliyor...', { videoId, language });
    
    // Yeni videoya geçerken UI state'i temizle
    videoStore.resetVideoData();

    // Video ID'sini ayarla
    videoStore.setVideoId(videoId);
    
    // Önce direkt database'den mevcut veriyi çek (hızlı)
    const directData = await apiService.getVideoDataDirect(videoId, language);
    
    if (directData.status === 'success' && directData.data) {
      console.log('✅ Direkt veri bulundu:', directData.data);
      
      // Mevcut veri varsa hemen göster
      if (directData.data.transcript || directData.data.summary || directData.data.video) {
        const updateData: any = {
          ...videoData.value,
          loading: false,
          error: null,
          id: videoId
        };

        // Transcript varsa ekle
        if (directData.data.transcript) {
          updateData.transcript = directData.data.transcript.formatted_text || '';
          updateData.transcriptPreview = (directData.data.transcript.formatted_text || '').substring(0, 400) + (directData.data.transcript.formatted_text ? '...' : '');
          updateData.formatted_text = directData.data.transcript.formatted_text || '';
          updateData.transcriptLanguage = directData.data.transcript.language || '';
        }

        // Summary varsa ekle
        if (directData.data.summary) {
          updateData.summary = directData.data.summary.content || '';
          updateData.summaryPreview = (directData.data.summary.content || '').substring(0, 400) + (directData.data.summary.content ? '...' : '');
          updateData.summaryLanguage = directData.data.summary.language || '';
        }

        // Video meta bilgilerini ekle (boş/undefined ise eski değeri taşımayalım)
        if (directData.data.video) {
          updateData.title = directData.data.video.title || '';
          updateData.description = directData.data.video.description || '';
          updateData.thumbnail = directData.data.video.thumbnail || '';
          updateData.channelTitle = directData.data.video.channelTitle || '';
          updateData.channelId = directData.data.video.channelId || '';
          updateData.publishedAt = directData.data.video.publishedAt || '';
          updateData.published_at = directData.data.video.publishedAt || '';
          updateData.duration = directData.data.video.duration || '';
          updateData.viewCount = directData.data.video.viewCount || 0;
        } else {
          // Video meta hiç yoksa başlığı kesin temiz bırak
          updateData.title = '';
        }

        // Store'u güncelle
        videoStore.setVideoData(updateData);
        
        console.log('🎯 Thumbnail verisi başarıyla yüklendi (direkt database)');
        return;
      }
    }
    
    // Eğer direkt veri yoksa normal işleme geç
    console.log('⚠️ Direkt veri bulunamadı, normal işleme geçiliyor...');
    await handleVideoProcess(videoId, language);
    
  } catch (error) {
    console.error('❌ Thumbnail click hatası:', error);
    // Hata durumunda normal işleme geç
    await handleVideoProcess(videoId, language);
  }
  
  // Eğer aktif sekme "listen" ise, yeni video için ses oluştur
  if (activeTab.value === 'listen' && videoData.value?.summary) {
    // Biraz bekleyerek videonun yüklenmesini sağla
    setTimeout(() => {
      textToSpeech();
    }, 100);
  }
};

// Add function to fetch summary data directly from database
const fetchSummaryDirect = async () => {
  if (!videoId.value) return;
  
  try {
    const fetchLang = (videoData.value as any)?.language || (videoStore as any)?.processingLanguage || selectedLanguage.value || languageStore.currentLocale;
    console.log('�� Summary tab - Direkt database\'den özet çekiliyor...', { 
      videoId: videoId.value, 
      language: fetchLang 
    });
    
    const directData = await apiService.getVideoDataDirect(videoId.value, fetchLang);
    
    if (directData.status === 'success' && directData.data?.summary) {
      console.log('✅ Summary direkt database\'den alındı');
      
      const updateData: any = {
        ...videoData.value,
        summary: directData.data.summary.content || '',
        summaryPreview: (directData.data.summary.content || '').substring(0, 400) + (directData.data.summary.content ? '...' : ''),
        summaryLanguage: directData.data.summary.language || '',
        language: (videoData.value as any)?.language || directData.data.summary.language || fetchLang
      };
      
      // Video meta bilgilerini de güncelle (boşsa önceki kalmasın)
      if (directData.data.video) {
        updateData.title = directData.data.video.title || '';
        updateData.description = directData.data.video.description || '';
        updateData.thumbnail = directData.data.video.thumbnail || '';
        updateData.channelTitle = directData.data.video.channelTitle || '';
        updateData.channelId = directData.data.video.channelId || '';
        updateData.publishedAt = directData.data.video.publishedAt || '';
        updateData.published_at = directData.data.video.publishedAt || '';
        updateData.duration = directData.data.video.duration || '';
        updateData.viewCount = directData.data.video.viewCount || 0;
      } else {
        updateData.title = '';
      }
      
      videoStore.setVideoData(updateData);
    } else {
      console.log('⚠️ Summary database\'de bulunamadı');
    }
  } catch (error) {
    console.error('❌ Summary direkt çekme hatası:', error);
    } finally {
     // Sadece summary bulunduysa spinnerı kapat
     const hasSummary = !!(videoStore.videoData as any)?.summary;
     if (hasSummary) {
       videoStore.setLoadingState('summary', false);
       videoStore.toggleSpinner('summary', false);
     }
   }
};

// Add function to fetch transcript data directly from database
const fetchTranscriptDirect = async () => {
  if (!videoId.value) return;
  
  let found = false;
  try {
    const fetchLang = (videoData.value as any)?.language || (videoStore as any)?.processingLanguage || selectedLanguage.value || languageStore.currentLocale;
    console.log('📝 Transcript tab - Direkt database\'den transcript çekiliyor...', { videoId: videoId.value, language: fetchLang });
    const directData = await apiService.getVideoDataDirect(videoId.value, fetchLang);
    
    if (directData.status === 'success' && directData.data?.transcript) {
      console.log('✅ Transcript direkt database\'den alındı');
      
      const updateData: any = {
        ...videoData.value,
        transcript: directData.data.transcript.formatted_text || '',
        transcriptPreview: (directData.data.transcript.formatted_text || '').substring(0, 400) + (directData.data.transcript.formatted_text ? '...' : ''),
        formatted_text: directData.data.transcript.formatted_text || '',
        transcriptLanguage: directData.data.transcript.language || '',
        language: (videoData.value as any)?.language || directData.data.transcript.language || fetchLang
      };
      
      // Video meta bilgilerini de güncelle (boşsa önceki kalmasın)
      if (directData.data.video) {
        updateData.title = directData.data.video.title || '';
        updateData.description = directData.data.video.description || '';
        updateData.thumbnail = directData.data.video.thumbnail || '';
        updateData.channelTitle = directData.data.video.channelTitle || '';
        updateData.channelId = directData.data.video.channelId || '';
        updateData.publishedAt = directData.data.video.publishedAt || '';
        updateData.published_at = directData.data.video.publishedAt || '';
        updateData.duration = directData.data.video.duration || '';
        updateData.viewCount = directData.data.video.viewCount || 0;
      } else {
        updateData.title = '';
      }
      
      videoStore.setVideoData(updateData);
      found = true;
    } else {
      console.log('⚠️ Transcript database\'de bulunamadı');
    }
  } catch (error) {
    console.error('❌ Transcript direkt çekme hatası:', error);
  } finally {
    // Transcript spinnerını yalnızca transcript bulunduysa kapat
    if (found) {
      videoStore.setLoadingState('transcript', false);
      videoStore.toggleSpinner('transcript', false);
    }
  }
};

// Add function to handle tab clicks with direct database fetching
const handleTabClick = async (tab: string) => {
  console.log('🔄 Tab değişikliği:', { from: activeTab.value, to: tab, videoId: videoId.value });
  
  // Tab'ı değiştir
  activeTab.value = tab;
  
  // Eğer video ID yoksa sadece tab değiştir
  if (!videoId.value) {
    console.log('⚠️ Video ID yok, sadece tab değiştirildi');
    return;
  }
  
  // Tab'a göre direkt database'den veri çek
  switch (tab) {
    case 'summary':
      // Summary tab'ına geçildiğinde ve summary verisi yoksa direkt database'den çek
      if (!videoData.value?.summary) {
        console.log('📄 Summary verisi yok, direkt database\'den çekiliyor...');
        await fetchSummaryDirect();
      } else {
        console.log('📄 Summary verisi mevcut, direkt çekme atlanıyor');
      }
      break;
      
    case 'transcript':
      // Transcript tab'ına geçildiğinde ve transcript verisi yoksa direkt database'den çek
      if (!videoData.value?.transcript) {
        console.log('📝 Transcript verisi yok, direkt database\'den çekiliyor...');
        await fetchTranscriptDirect();
      } else {
        console.log('📝 Transcript verisi mevcut, direkt çekme atlanıyor');
      }
      break;
      
    case 'listen':
      // Listen tab'ına geçildiğinde önce mevcut TTS URL'sini kontrol et
      if (videoData.value?.summary && !isLoadingAudio.value) {
        console.log('🎵 Listen tab\'ına geçildi, mevcut TTS URL kontrol ediliyor...');
        setTimeout(async () => {
          console.log('🔍 checkExistingTTS fonksiyonu çağrılıyor...');
          const hasExistingTTS = await checkExistingTTS();
          console.log('📊 checkExistingTTS sonucu:', hasExistingTTS);
          if (!hasExistingTTS) {
            console.log('❌ Mevcut TTS bulunamadı, yeni TTS oluşturuluyor');
            textToSpeech();
          } else {
            console.log('✅ Mevcut TTS bulundu, yeni TTS oluşturulmuyor');
          }
        }, 100);
      }
      break;
  }
};

// Component oluşturulduğunda
onMounted(async () => {
  console.log('🔄 Component mounted');
  
  // SEO meta tags'leri güncelle
  updateHomePageSEO();
  
  // Click listener'ı ekle
  document.addEventListener('click', closeMenu);
  
  // Spinner güvenlik zamanlayıcısı (3 dakika sonra spinnerlar hala dönüyorsa kapat)
  setSummaryTimeout(videoData.value?.id, 'spinnerSafety', () => {
    if (videoData.value?.id && (
      videoStore.getLoadingState('summary') || 
      videoStore.getLoadingState('transcript') || 
      videoStore.getLoadingState('processing')
    )) {
      console.log('⚠️ Safety timeout: Force closing spinners after 3 minutes');
      forceCloseSpinners(videoData.value.id);
      // Polling service durumlarını sıfırla
      pollingService.isLoadingTranscript.value = false;
      pollingService.isLoadingSummary.value = false;
      pollingService.isPollingActiveSummary.value = false;
    }
  }, 180000); // 3 dakika
  
  // Dil değişikliği listener'ı ekle - debounce ile gecikme ekleniyor
  const handleLanguageChange = debounce((newLang: string) => {
    console.log(`🌍 Dil değişikliği algılandı: ${newLang}`);
    
    // YENİ KOD: Dil değişikliği sadece UI'ı değiştiriyor, içeriği değil
    console.log('🌍 Sayfa dili değiştirildi, içerik dili korunuyor');
    
    // Video işleme dilini DEĞİŞTİRMİYORUZ, sadece UI dilini değiştiriyoruz
    // Özet ve transkript özet diline bağlı kalacak, dil değişiminden etkilenmeyecek
    
    // Alt bileşenleri yeniden render etmek için forceRender'ı artır
    forceRender.value++;
    
    // SEO meta tags'leri güncelle
    updateHomePageSEO();
  }, 500); // 500ms gecikme ile

  // Temizleme fonksiyonunu sakla
  cleanupLanguageListener = languageStore.onLanguageChange(handleLanguageChange);
  console.log('🌐 Dil değişikliği dinleyicisi eklendi');
  
  // Default video ID'sini ayarla
  const defaultVideoId = 'ithXe2krO9A';
  videoStore.setVideoId(defaultVideoId);
  
  // Default video yüklemesi - Önce direkt database'den çek, yoksa queue'ya gönder
  console.log('🎬 Loading default video with optimized system:', defaultVideoId);
  try {
    // Landing state için default EN yükleme; işlem başlatıldıysa atla
    if (!videoStore.processingLanguage) {
      const directData = await apiService.getVideoDataDirect(defaultVideoId, 'en');
      
      if (directData.status === 'success' && directData.data && (directData.data.summary || directData.data.transcript)) {
        console.log('✅ Default video verisi database\'de bulundu, direkt yükleniyor');
        
        const updateData: any = {
          ...videoData.value,
          id: defaultVideoId,
          loading: false,
          error: null
        };

        // Transcript varsa ekle
        if (directData.data.transcript) {
          updateData.transcript = directData.data.transcript.formatted_text || '';
          updateData.transcriptPreview = directData.data.transcript.formatted_text?.substring(0, 400) + '...' || '';
          updateData.formatted_text = directData.data.transcript.formatted_text || '';
        }

        // Summary varsa ekle
        if (directData.data.summary) {
          updateData.summary = directData.data.summary.content || '';
          updateData.summaryPreview = directData.data.summary.content?.substring(0, 400) + '...' || '';
        }

        // Video meta bilgilerini ekle
        if (directData.data.video) {
          updateData.title = directData.data.video.title || '';
          updateData.description = directData.data.video.description || '';
          updateData.thumbnail = directData.data.video.thumbnail || '';
          updateData.channelTitle = directData.data.video.channelTitle || '';
          updateData.channelId = directData.data.video.channelId || '';
          updateData.publishedAt = directData.data.video.publishedAt || '';
          updateData.published_at = directData.data.video.publishedAt || '';
          updateData.duration = directData.data.video.duration || '';
          updateData.viewCount = directData.data.video.viewCount || 0;
        }

        // Store'u güncelle
        videoStore.setVideoData(updateData);
        
        console.log('🎯 Default video başarıyla yüklendi (direkt database)');
      } else {
        // Eğer direkt veri yoksa queue'ya gönder
        console.log('⚠️ Default video database\'de bulunamadı, queue\'ya gönderiliyor...');
        
        const result = await NewVideoProcessingService.processVideoComplete(defaultVideoId, 'en');
        
        if (result.status === 'success' && result.data) {
          // Sonuçları store'a kaydet
          videoStore.setVideoData({
            ...videoData.value,
            id: defaultVideoId,
            transcript: result.data.transcript?.formatted_text || '',
            transcriptPreview: result.data.transcript?.formatted_text?.substring(0, 400) + '...' || '',
            summary: result.data.summary?.content || '',
            summaryPreview: result.data.summary?.content?.substring(0, 400) + '...' || '',
            formatted_text: result.data.transcript?.formatted_text || '',
            loading: false,
            error: null
          });
          console.log('✅ Default video queue\'dan başarıyla yüklendi');
        }
      }
      
      // Video işleme dili olarak 'en' kullanıldığı için, özetleri yüklerken de bu dili kullanacağız
      console.log(`📝 Default özetler işleme dili ile yükleniyor: en (UI dili: ${languageStore.currentLocale})`);
      summaries.value = await videoProcessingService.loadAvailableSummaries({ language: 'en' });
    }
    
    // Fetch recent summaries
    await fetchRecentSummaries();
  } catch (err) {
    console.error('Error loading default video:', err);
    error.value = err instanceof Error ? err.message : 'Failed to load default video';
  }
});

onUnmounted(() => {
  console.log('🔄 Component unmounting');
  
  // Click listener'ı kaldır
  document.removeEventListener('click', closeMenu);
  
  // Polling'i durdur
  if (videoData.value?.id) {
    console.log(`🛑 Stopping all polling for video ${videoData.value.id} during component unmount`);
    pollingService.stopAllPolling(videoData.value.id);
  }
  
  // Tüm aktif polling'leri durdur (video ID'den bağımsız)
  console.log(`🚨 Stopping all active polling processes during component unmount`);
  pollingService.stopAllActivePolling();
  
  // Dil değişikliği dinleyicisini temizle
  if (cleanupLanguageListener) {
    cleanupLanguageListener();
    console.log('🌐 Dil değişikliği dinleyicisi temizlendi');
  }
  
  // Tarayıcı konsoluna acil durumda kullanılabilecek fonksiyonu ekle
  if (typeof window !== 'undefined' && isDev.value) {
    console.log('💡 Acil durum fonksiyonu tarayıcı konsoluna eklendi:');
    console.log('   window.emergencyStopPolling() kullanarak tüm polling işlemlerini durdurabilirsiniz');
    (window as any).emergencyStopPolling = emergencyStopAllPolling;
  }
  
  console.log('🧹 Component unmount temizliği tamamlandı');
});

// Add beforeUnmount hook as an extra safety measure
onBeforeUnmount(() => {
  console.log('🔄 Component before unmount - performing final cleanup');
  
  // Force stop all polling one more time
  if (videoData.value?.id) {
    console.log(`🛑 Final stop of all polling for video ${videoData.value.id} before unmount`);
    pollingService.stopAllPolling(videoData.value.id);
  }
  
  // Tüm aktif polling'leri son bir kez durdur
  console.log('🧨 Emergency stopping all active polling before unmount');
  pollingService.stopAllActivePolling();
  
  // Polling service'in acil durum fonksiyonunu çağır
  if (typeof pollingService.emergencyStopAllPolling === 'function') {
    console.log('☢️ Using nuclear option to clear ALL intervals');
    pollingService.emergencyStopAllPolling();
  }
  
  console.log('🧹 Final component cleanup completed');

  if (ttsAudio.value) {
    ttsAudio.value.removeEventListener('ended', handleTTSEnded);
    ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
  }
  stopAudio();
});

// Emergency stop function for debug panel
const emergencyStopAllPolling = () => {
  console.log('🔥 EMERGENCY: STOPPING ALL POLLING PROCESSES');
  // Normal polling durdurma
  if (videoData.value?.id) {
    pollingService.stopAllPolling(videoData.value.id);
  }
  // Tüm aktif polling'leri durdur
  pollingService.stopAllActivePolling();
  // Nuclear option: tüm interval ve timeout'ları temizle
  if (typeof pollingService.emergencyStopAllPolling === 'function') {
    pollingService.emergencyStopAllPolling();
  } else {
    // Fallback: manuel olarak interval'ları temizle
    console.log('⚠️ emergencyStopAllPolling method not found, using manual cleanup');
    for (let i = 0; i < 10000; i++) {
      try {
        window.clearInterval(i);
        window.clearTimeout(i);
      } catch (e) {
        // Hataları görmezden gel
      }
    }
  }
  // Loading ve spinner durumlarını temizle
  forceCloseSpinners(videoData.value?.id);
  console.log('✅ Emergency stop completed: All polling stopped and intervals cleared');
  return "All polling processes stopped successfully";
};

// Component unmount olduğunda interval'ları temizle
const clearAllIntervals = () => {
  console.log('🧹 Clearing all intervals');
  // Implement as needed for additional interval clearing
};

// Yeniden deneme işlevi
const retryProcessing = () => {
  console.log('🔄 Retrying video processing');
  if (videoData.value?.id) {
    error.value = '';
    if (videoData.value.id) {
      pollingService.stopAllPolling(videoData.value.id);
    }
    showLanguageModal.value = true;  // Kullanıcı tekrar dil seçmeli
  }
};

const handleSearch = async () => {
  try {
    if (!searchQuery.value) return;
    
    // Spinner'ları aktif et
    videoStore.setLoadingState('video', true);
    videoStore.toggleSpinner('video', true);
    videoStore.setLoadingState('processing', true);
    videoStore.toggleSpinner('processing', true);
    
    // Video ID'yi çıkar
    const extractedVideoId = await newHandleSearch(searchQuery.value);
    if (!extractedVideoId) {
      notifyError('Geçersiz YouTube URL\'si');
      return;
    }
    
    // Yeni URL girdiğinde TTS verilerini tamamen temizle
    console.log('🧹 Yeni URL giriliyor, TTS verileri temizleniyor...');
    
    // Audio elementlerini temizle
    if (ttsAudio.value) {
      ttsAudio.value.pause();
      ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
      ttsAudio.value.removeEventListener('ended', handleTTSEnded);
      ttsAudio.value.src = '';
      ttsAudio.value.load();
      ttsAudio.value = null;
    }
    
    if (backgroundAudio.value) {
      backgroundAudio.value.pause();
      backgroundAudio.value.currentTime = 0;
    }
    
    // Audio URL'i temizle
    if (audioUrl.value) {
      try {
        URL.revokeObjectURL(audioUrl.value);
        console.log('🗑️ Eski audio URL temizlendi:', audioUrl.value);
      } catch (err) {
        console.error('Audio URL temizlenirken hata:', err);
      }
      audioUrl.value = '';
    }
    
    // Audio durumlarını sıfırla
    isPlaying.value = false;
    audioProgress.value = 0;
    currentTime.value = '0:00';
    totalDuration.value = '0:00';
    isLoadingAudio.value = false;
    
    console.log('✅ TTS verileri başarıyla temizlendi');

    // Video verilerini güncelle
    videoStore.setVideoData({
      id: extractedVideoId,
      url: searchQuery.value,
      loading: true,
      error: null,
      // Önceki içeriği temizle
      transcript: '',
      transcriptPreview: '',
      summary: '',
      summaryPreview: '',
      formatted_text: ''
    });
    
    // Video ID'sini ayarla
    videoStore.setVideoId(extractedVideoId);
    
    // Geçici URL'yi sakla
    pendingVideoUrl.value = searchQuery.value;
    
    // İşlem dilini sıfırla (yeni video için yeni dil seçimi yapılacak)
    videoStore.setProcessingLanguage('');
    
    // Dil seçim modalını göster
    console.log('🌐 Opening language selection modal for video processing');
    showLanguageModal.value = true;
  } catch (err) {
    console.error('Error in handleSearch:', err);
    const errorMessage = err instanceof Error ? err.message : 'Video aranırken bir hata oluştu';
    error.value = errorMessage;
    notifyError(errorMessage);
  } finally {
    // Video spinner'ını kapat (diğerleri processVideoWithLanguage'da yönetilecek)
    videoStore.setLoadingState('video', false);
    videoStore.toggleSpinner('video', false);
  }
};

const processVideoWithLanguage = async (language: string) => {
  console.log('🔍 Video işleme başlatılıyor vecizai | Seçilen dil:', language, 'Video ID:', videoId.value);
  
  try {
    // Geçerli bir video ID'si kontrol et
    if (!videoId.value) {
      console.error('❌ Video ID bulunamadı!');
      error.value = 'Video ID bulunamadı';
      notifyError('Video ID bulunamadı');
      return;
    }
    
    // Spinner'ları hemen aktif et (kullanıcı feedback almalı)
    videoStore.setLoadingState('transcript', true);
    videoStore.toggleSpinner('transcript', true);
    videoStore.setLoadingState('summary', true);
    videoStore.toggleSpinner('summary', true);
    videoStore.setLoadingState('processing', true);
    videoStore.toggleSpinner('processing', true);
    
    // Seçilen dili video işleme dili olarak ayarla
    videoStore.setProcessingLanguage(language);
    console.log('🌐 Video işleme dili ayarlandı:', language);
    
    console.log('🚀 Ana sayfa için direkt video işlemi başlatılıyor vecizai...');
    
    // Ana sayfa için direkt video işlemi - YENİ SİSTEM 
    // handleVideoProcess fonksiyonunu çağır (spinner yönetimi burada)
    await handleVideoProcess(videoId.value, language);
  } catch (err) {
    console.error('❌ processVideoWithLanguage hatası:', err);
    error.value = err instanceof Error ? err.message : 'Video işleme hatası vecizai';
    notifyError(error.value);
    
    // Hata durumunda spinner'ları kapat
    videoStore.setLoadingState('transcript', false);
    videoStore.toggleSpinner('transcript', false);
    videoStore.setLoadingState('summary', false);
    videoStore.toggleSpinner('summary', false);
    videoStore.setLoadingState('processing', false);
    videoStore.toggleSpinner('processing', false);
  }
};

// Summary tipi tanımları
interface SummaryStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  content?: string | null;
  task_id?: string;
  error?: string;
}

const submitFeedback = () => {
  if (!selectedSummary.value) return
  // Show success message or notification
  notifyWarning(languageStore.t('summaries.feedback.submitSuccess'))
}

const openSummary = async (summary: any) => {
  try {
    showDetailModal.value = true;
    await markSummaryAsRead(summary);
  } catch (error) {
    console.error('Error opening summary:', error);
  }
};

const closeSummary = () => {
  showDetailModal.value = false;
};

const shareTranscript = () => {
  if (navigator.share) {
    navigator.share({
      title: videoData.value.title,
      text: videoData.value.transcript,  // Share the full transcript text
      url: `https://www.youtube.com/watch?v=${videoId.value}`
    })
  } else {
    const textArea = document.createElement('textarea')
    textArea.value = `${videoData.value.title}\n\n${videoData.value.transcript}\n\nVideo: https://www.youtube.com/watch?v=${videoId.value}`  // Share the full transcript text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    notifyWarning(languageStore.t('common.copiedToClipboard'))
  }
}

const downloadTranscript = () => {
  const content = `${videoData.value.title}\n\n${videoData.value.transcript}\n\nVideo: https://www.youtube.com/watch?v=${videoId.value}`  // Download the full transcript text
  const blob = new Blob([content], { type: 'text/plain' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${videoData.value.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_transcript.txt`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

const shareSummary = () => {
  if (navigator.share) {
    navigator.share({
      title: videoData.value.title,
      text: videoData.value.summary,
      url: `https://www.youtube.com/watch?v=${videoId.value}`
    })
  } else {
    const textArea = document.createElement('textarea')
    textArea.value = `${videoData.value.title}\n\n${videoData.value.summary}\n\nVideo: https://www.youtube.com/watch?v=${videoId.value}`
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    notifyWarning(languageStore.t('common.copiedToClipboard'))
  }
}

const downloadSummary = () => {
  const content = `${videoData.value.title}\n\n${videoData.value.summary}\n\nVideo: https://www.youtube.com/watch?v=${videoId.value}`
  const blob = new Blob([content], { type: 'text/plain' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${videoData.value.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_summary.txt`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

const handleVideoProcess = async (videoId: string, language: string) => {
  console.log('🎬 Ana sayfa handleVideoProcess başlatılıyor...', { videoId, language });
  
  // Yeni bir işlem başlarken önceki veriyi tamamen temizle ve aktif videoId'yi ayarla
  videoStore.resetVideoData();
  videoStore.setVideoId(videoId);
  
  let result;
  try {
    result = await NewVideoProcessingService.processVideoComplete(videoId, language);
    console.log('🔍 handleVideoProcess result:', result);
  } catch (err: any) {
    // Axios veya beklenmeyen hata
    error.value = err?.response?.data?.message || err?.message || 'Video işleme hatası';
    notifyError(error.value);
    // Spinnerları kapat
    videoStore.setLoadingState('transcript', false);
    videoStore.toggleSpinner('transcript', false);
    videoStore.setLoadingState('summary', false);
    videoStore.toggleSpinner('summary', false);
    videoStore.setLoadingState('processing', false);
    videoStore.toggleSpinner('processing', false);
    return;
  }

  // NewVideoProcessingService'den gelen yanıt: success veya completed başarı kabul edilir
  if ((result.status === 'success' || result.status === 'completed') && result.data) {
    console.log('✅ Video işlemi başarıyla başlatıldı! Dil:', language);
    
    // Eğer queued flag'i varsa, işlem devam ediyor demektir
    if (result.queued) {
      console.log('🔄 Video işleme devam ediyor, polling sistemi aktif...');
      // Polling sistemi spinner'ları yönetecek
      return;
    }
    
    // Eğer direkt tamamlanmış data varsa
    if (result.data?.transcript || result.data?.summary) {
      console.log('🎯 Video işlemi tamamlandı, sonuçları yükleniyor...');
      
      const updateData: any = {
        ...videoData.value,
        transcript: result.data.transcript?.formatted_text || '',
        transcriptPreview: result.data.transcript?.formatted_text?.substring(0, 200) + '...' || '',
        summary: result.data.summary?.content || '',
        summaryPreview: result.data.summary?.content?.substring(0, 200) + '...' || '',
        formatted_text: result.data.transcript?.formatted_text || '',
        loading: false,
        error: null,
        language: result.data.language || language
      };

      // Video meta bilgilerini de güncelle
      if (result.data.video) {
        updateData.title = result.data.video.title || '';
        updateData.description = result.data.video.description || '';
        updateData.thumbnail = result.data.video.thumbnail || '';
        updateData.channelTitle = result.data.video.channelTitle || '';
        updateData.channelId = result.data.video.channelId || '';
        updateData.publishedAt = result.data.video.publishedAt || '';
        updateData.duration = result.data.video.duration || '';
        updateData.viewCount = result.data.video.viewCount || 0;
      }

      videoStore.setVideoData(updateData);
      
      // İşlem tamamlandığında spinner'ları kapat
      videoStore.setLoadingState('transcript', false);
      videoStore.toggleSpinner('transcript', false);
      videoStore.setLoadingState('summary', false);
      videoStore.toggleSpinner('summary', false);
      videoStore.setLoadingState('processing', false);
      videoStore.toggleSpinner('processing', false);
    }
    
    return;
  }
  
  // Sadece gerçek hata durumunda (status: 'error') hata fırlat
  if (result?.status === 'error') {
    // Eğer backend'den rate limit veya açıklayıcı bir mesaj geldiyse onu göster
    error.value = result.error || result.message || 'Video işleme başarısız';
    notifyError(error.value);
    // Spinnerları kapat
    videoStore.setLoadingState('transcript', false);
    videoStore.toggleSpinner('transcript', false);
    videoStore.setLoadingState('summary', false);
    videoStore.toggleSpinner('summary', false);
    videoStore.setLoadingState('processing', false);
    videoStore.toggleSpinner('processing', false);
    return;
  }
  // Diğer durumlarda (örn. already-completed), spinnerları kapatıp çık
  videoStore.setLoadingState('transcript', false);
  videoStore.toggleSpinner('transcript', false);
  videoStore.setLoadingState('summary', false);
  videoStore.toggleSpinner('summary', false);
  videoStore.setLoadingState('processing', false);
  videoStore.toggleSpinner('processing', false);
  return;
};

const processVideo = async (url: string) => {
  try {
    forceCloseSpinners(videoId.value);
    error.value = '';

    // Hard reset UI state to prevent previous video's data leaking
    videoStore.resetVideoData();
    audioUrl.value = '';
    isPlaying.value = false;
 
    // Extract video ID
    const extractedVideoId = getVideoId(url);
    if (!extractedVideoId) {
      throw new Error('Invalid YouTube URL');
    }
    videoStore.setVideoId(extractedVideoId);  // videoStore üzerinden güncelle

    // Create transcript
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcripts/from-video`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        videoId: extractedVideoId,  // videoId.value yerine extractedVideoId kullan
        language: 'tr'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create transcript');
    }

    const data = await response.json();
    console.log('📝 Transcript task created:', data);

    await new Promise(resolve => setTimeout(resolve, 2000));
    // await videoProcessingService.pollTranscriptStatus(videoId.value, 'tr');
    console.log('✅ Transcript is ready, creating summary...');

    // Create summary
    const summaryResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/videos/${videoId.value}/summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        videoId: videoId.value,
        language: 'tr'
      })
    });

    if (!summaryResponse.ok) {
      throw new Error('Failed to create summary');
    }

    const summaryData = await summaryResponse.json();
    console.log('📚 Summary task created:', summaryData);

    // Eğer özet zaten tamamlanmışsa spinner'ı hemen durduralım
    if (summaryData.status === 'completed') {
      console.log('✅ Summary already completed, stopping spinner immediately');
      forceCloseSpinners(videoId.value);
    } else {
      await new Promise(resolve => setTimeout(resolve, 2000));
      // await videoProcessingService.pollSummaryStatus(videoId.value, 'tr');
      console.log('✅ Summary is ready!');
    }

    // Clear processing status
    forceCloseSpinners(videoId.value);

  } catch (err) {
    console.error('❌ Error in processVideo:', err);
    forceCloseSpinners(videoId.value);
    error.value = err instanceof Error ? err.message : 'Failed to process video';
  }
};

// Kullanılabilir özetleri yüklemek için fonksiyon 
const loadAvailableSummaries = async () => {
  try {
    console.log('🚀 Loading available summaries for home view');
    // Önemli değişiklik: Özet dilini UI dilinden bağımsız olarak yükle
    // videoStore'dan mevcut işleme dilini alıyoruz veya varsayılan olarak 'en' kullanıyoruz
    const processingLanguage = videoStore.processingLanguage || 'en';
    console.log(`📝 Özetler işleme dili ile yükleniyor: ${processingLanguage} (UI dili: ${languageStore.currentLocale})`);
    
    // Sadece dil parametresi ekliyoruz - UI dilini DEĞİL, video işleme dilini kullanıyoruz
    summaries.value = await newLoadAvailableSummaries({ language: processingLanguage });
    console.log('✅ Loaded summaries:', summaries.value.length);
  } catch (err) {
    console.error('❌ Error loading summaries:', err);
    error.value = err instanceof Error ? err.message : 'Failed to load summaries';
  }
};

const markSummaryAsRead = async (summary: any) => {
  // Only proceed if user is logged in and summary exists and is not read
  if (!authStore.session) {
    console.log('Skipping mark as read: User not logged in');
    return;
  }
  
  // Check if this is a saved summary with an ID (not just the current video)
  if (!summary || !summary.id) {
    console.log('Skipping mark as read: Not a stored summary');
    return;
  }
  
  // Skip if already marked as read
  if (summary.isRead) {
    console.log('Skipping mark as read: Already read');
    return;
  }
  
  try {
    console.log('Marking summary as read:', summary.id);
    
    // First check if API is available
    const isApiAvailable = await checkApiConnection();
    
    if (!isApiAvailable) {
      console.warn('API server is not available, skipping mark as read');
      return;
    }
    
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/summaries/${summary.id}/mark-read`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authStore.session?.access_token}`
      }
    });

    if (!response.ok) {
      console.error('Failed to mark summary as read, status:', response.status);
    }
  } catch (error) {
    console.error('Error marking summary as read:', error);
    // Not showing error to user since this is non-critical functionality
  }
};

// Check if API is available
const checkApiConnection = async (): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
    
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/health`, {
      method: 'GET',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (e) {
    return false;
  }
};

const handleSummaryClick = async (summary: any) => {
  try {
    showDetailModal.value = true;
    
    // Try to mark as read but don't wait for it to complete
    markSummaryAsRead(summary).catch(err => {
      console.warn('Non-critical error marking summary as read:', err);
    });
  } catch (error) {
    console.error('Error handling summary click:', error);
  }
};

// Update status check comparisons
const isTranscriptCompleted = (status: string) => status === 'completed';
const isSummaryCompleted = (status: string) => status === 'completed';

// Add formattedSummary computed property
const formattedSummary = computed(() => {
  if (!videoData.value?.summary) return '';
  const summaryText = videoData.value.summary.trim();
  return FormatService.formatSummaryText(summaryText);
});

// Add formattedSummaryPreview computed property
const formattedSummaryPreview = computed(() => {
  if (!videoData.value?.summary) return '';
  const maxLength = 650;
  const summaryText = videoData.value.summary
    .trim()
    .replace(/\n\s*\n/g, ' ') // Replace multiple newlines with a single space
    .replace(/\n/g, ' ') // Replace single newlines with a space
    .replace(/\s+/g, ' '); // Replace multiple spaces with a single space
  
  const truncatedText = summaryText.length > maxLength 
    ? summaryText.substring(0, maxLength).trim() + '...' 
    : summaryText;
  return FormatService.formatSummaryText(truncatedText);
});

// Ses dosyalarını oynat (SummariesView'dan kopyalanan çalışan versiyon)
const playAudio = () => {
  if (!ttsAudio.value || !backgroundAudio.value) return;
  
  // TTS ses ayarları
  ttsAudio.value.volume = ttsVolume.value;
  
  // Eğer ses baştan başlatılıyorsa (currentTime 0 ise)
  if (ttsAudio.value.currentTime === 0) {
    backgroundAudio.value.currentTime = 0;
    
    // Mobil için düşük, masaüstü için orta seviyeden başla
    const isMobile = window.innerWidth < 640; // Tailwind'in sm breakpoint'i
    
    if (isMobile) {
      // Mobilde düşük ses ile başla
      backgroundAudio.value.volume = bgVolume.value * 0.3;
    } else {
      // Masaüstünde orta seviyeden başla
      backgroundAudio.value.volume = bgVolume.value * 0.5;
    }
  } else {
    // Devam ediyorsa normal ses seviyesini kullan
    backgroundAudio.value.volume = bgVolume.value;
  }
  
  ttsAudio.value.play();
  
  // Fon müziği ayarları
  backgroundAudio.value.loop = true;
  backgroundAudio.value.play();
  
  isPlaying.value = true;
};

const stopAudio = () => {
  if (ttsAudio.value) {
    ttsAudio.value.pause();
  }
  
  // Arka plan müziğini de durdur
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
  }
  
  isPlaying.value = false;
};

const resetAudio = () => {
  console.log('🔄 Audio reset işlemi başlatılıyor...');
  
  if (ttsAudio.value) {
    ttsAudio.value.pause();
    ttsAudio.value.currentTime = 0;
    // Event listener'ları temizle
    ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
    ttsAudio.value.removeEventListener('ended', handleTTSEnded);
    audioProgress.value = 0;
    currentTime.value = '0:00';
  }
  
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
    backgroundAudio.value.currentTime = 0;
  }
  
  // Audio URL'i temizle
  if (audioUrl.value) {
    try {
      URL.revokeObjectURL(audioUrl.value);
      console.log('🗑️ Audio URL temizlendi:', audioUrl.value);
    } catch (err) {
      console.error('Audio URL temizlenirken hata:', err);
    }
    audioUrl.value = '';
  }
  
  isPlaying.value = false;
  console.log('✅ Audio reset tamamlandı');
};

const handleTTSEnded = () => {
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
    backgroundAudio.value.currentTime = 0;
  }
  isPlaying.value = false;
};

// Mevcut TTS URL'sini kontrol eden fonksiyon (HomeView için)
const checkExistingTTS = async (): Promise<boolean> => {
  if (!videoData.value?.summary) return false;
  
  console.log('🔍 HomeView - Mevcut TTS URL kontrol ediliyor...');
  console.log('📋 Video ID:', videoData.value?.id);
  console.log('📋 Video Language:', videoData.value?.language);
  
  try {
    // Video ID ve dil ile Supabase'den TTS URL'ini sorgula
    const currentVideoId = videoData.value?.id || videoId.value;
    const videoLanguage = videoData.value?.language || 'en';
    
    console.log('🔍 Supabase sorgusu yapılıyor, videoId:', currentVideoId, 'language:', videoLanguage);
    
    const { data, error } = await supabase
      .from('summaries')
      .select('tts_url')
      .eq('video_id', currentVideoId)
      .eq('language', videoLanguage)
      .single();
    
    if (error) {
      console.error('❌ Supabase sorgu hatası:', error);
    } else if (data?.tts_url) {
      console.log('✅ Supabase\'den TTS URL bulundu:', data.tts_url);
      
      // URL'nin erişilebilir olup olmadığını kontrol et
      try {
        // TTS URL'si tam URL ise direkt kullan, değilse API URL'sini ekle
        const audioUrlToFetch = data.tts_url.startsWith('http') 
          ? data.tts_url 
          : `${import.meta.env.VITE_API_URL}${data.tts_url}`;
        console.log('🔗 Fetch edilecek URL:', audioUrlToFetch);
        
        const audioResponse = await fetch(audioUrlToFetch);
        if (audioResponse.ok) {
          console.log('✅ TTS URL erişilebilir, mevcut TTS kullanılacak');
          
          // Audio nesnesini oluştur
          const audioBlob = await audioResponse.blob();
          const newAudioUrl = URL.createObjectURL(audioBlob);
          
          // Eski URL'yi temizle
          if (audioUrl.value) {
            try {
              URL.revokeObjectURL(audioUrl.value);
            } catch (err) {
              console.error('Eski audio URL temizlenirken hata:', err);
            }
          }
          
          audioUrl.value = newAudioUrl;
          
          // Audio nesnesini oluştur
          ttsAudio.value = new Audio(newAudioUrl);
          ttsAudio.value.addEventListener('timeupdate', updateAudioProgress);
          ttsAudio.value.addEventListener('ended', handleTTSEnded);
          ttsAudio.value.volume = ttsVolume.value;
          
          // Background audio setup
          if (!backgroundAudio.value) {
            backgroundAudio.value = new Audio('/audio/background.mp3');
            backgroundAudio.value.loop = true;
            backgroundAudio.value.volume = bgVolume.value;
          }
          
          return true;
        }
      } catch (fetchError) {
        console.warn('TTS URL erişilemez:', fetchError);
      }
    }
    
    console.log('Mevcut TTS bulunamadı');
    return false;
  } catch (error) {
    console.error('TTS kontrol sırasında hata:', error);
    return false;
  }
};

// Text-to-Speech fonksiyonu
const textToSpeech = async () => {
  if (!videoData.value?.summary) return;
  
  console.log('🎵 TTS fonksiyonu başlatılıyor...');
  
  // Önce mevcut TTS URL'sini kontrol et
  const hasExistingTTS = await checkExistingTTS();
  if (hasExistingTTS) {
    console.log('✅ Mevcut TTS bulundu, yeni TTS oluşturulmuyor');
    return;
  }
  
  console.log('❌ Mevcut TTS bulunamadı, yeni TTS oluşturuluyor');
  
  // Önce database'de bu video için TTS var mı kontrol et
  const currentVideoId = videoData.value?.id || videoId.value;
  
  // Video data'dan dili al (önce summary'den, yoksa transcript'ten)
  let videoLanguage = 'en'; // varsayılan
  
  // Önce mevcut videoData'dan dil bilgisini almaya çalış
  if (videoData.value?.summaryLanguage) {
    videoLanguage = videoData.value.summaryLanguage;
    console.log('🌐 VideoData.summaryLanguage\'dan alınan dil:', videoLanguage);
  } else if (videoData.value?.transcriptLanguage) {
    videoLanguage = videoData.value.transcriptLanguage;
    console.log('🌐 VideoData.transcriptLanguage\'dan alınan dil:', videoLanguage);
  } else {
    // Özet varsa dil bilgisi de olmalı, basit içerik analizi yap
    if (videoData.value?.summary) {
      // İngilizce yaygın kelimeler kontrol et
      const englishWords = ['the', 'and', 'is', 'in', 'to', 'of', 'a', 'that', 'it', 'with'];
      const summaryLower = videoData.value.summary.toLowerCase();
      const hasEnglish = englishWords.some(word => summaryLower.includes(` ${word} `));
      
      videoLanguage = hasEnglish ? 'en' : 'tr';
      console.log('🌐 Özet içeriğinden tespit edilen dil:', videoLanguage);
    }
  }
  
  if (currentVideoId) {
    const { data: existingTts, error: ttsError } = await supabase
      .from('summaries')
      .select('tts_url')
      .eq('video_id', currentVideoId)
      .eq('language', videoLanguage) // Video data'dan alınan dil
      .not('tts_url', 'is', null)
      .limit(1);

    if (!ttsError && existingTts?.[0]?.tts_url) {
      console.log('✅ Database\'de TTS bulundu:', existingTts[0].tts_url);
      
      try {
        const audioResponse = await fetch(`${import.meta.env.VITE_API_URL}${existingTts[0].tts_url}`);
        if (audioResponse.ok) {
          const audioBlob = await audioResponse.blob();
          audioUrl.value = URL.createObjectURL(audioBlob);
          
          // Audio elementlerini oluştur
          if (!ttsAudio.value) {
            ttsAudio.value = new Audio(audioUrl.value);
            ttsAudio.value.addEventListener('ended', handleTTSEnded);
            ttsAudio.value.addEventListener('timeupdate', updateAudioProgress);
          } else {
            ttsAudio.value.src = audioUrl.value;
          }
          
          if (!backgroundAudio.value) {
            backgroundAudio.value = new Audio('/audio/background.mp3');
            backgroundAudio.value.loop = true;
          }
          
          console.log('✅ Mevcut TTS başarıyla yüklendi, yeni oluşturmaya gerek yok');
          return; // Mevcut TTS bulundu, fonksiyondan çık
        }
      } catch (error) {
        console.warn('⚠️ Mevcut TTS yüklenemedi, yeni oluşturulacak:', error);
      }
    } else {
      console.log('⚠️ Database\'de TTS bulunamadı, yeni oluşturulacak');
    }
  }
  
  // Mevcut TTS yoksa normal TTS oluşturma işlemine devam et
  console.log('🔄 Yeni TTS oluşturuluyor...');
  
  // Önce mevcut audio verilerini tamamen temizle
  if (ttsAudio.value) {
    ttsAudio.value.pause();
    ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
    ttsAudio.value.removeEventListener('ended', handleTTSEnded);
    ttsAudio.value.src = '';
    ttsAudio.value.load();
    ttsAudio.value = null;
  }
  
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
    backgroundAudio.value.currentTime = 0;
  }
  
  // Önceki audio URL'i temizle
  if (audioUrl.value) {
    try {
      URL.revokeObjectURL(audioUrl.value);
      console.log('🗑️ TTS başlangıcında eski audio URL temizlendi');
    } catch (err) {
      console.error('Audio URL temizlenirken hata:', err);
    }
  }
  
  // Audio durumlarını sıfırla
  isPlaying.value = false;
  audioProgress.value = 0;
  currentTime.value = '0:00';
  totalDuration.value = '0:00';
  
  // Normal TTS oluşturma işlemi
  const maxRetries = 3;
  let retryCount = 0;
  
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  
  const attemptTTS = async (language: string) => {
    try {
      isLoadingAudio.value = true;
      audioUrl.value = ''; // Önceki URL'i temizle
      
      console.log(`TTS denemesi ${retryCount + 1}/${maxRetries}`);
      
      // Parametre olarak gelen dili kullan
      const selectedVoice = language === 'tr' ? 'tr-TR-Wavenet-E' : 'en-US-Wavenet-I';
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/tts/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: videoData.value.summary,
          languageCode: language === 'tr' ? 'tr-TR' : 'en-US',
          ssmlGender: 'MALE',
          voiceName: selectedVoice,
          videoId: currentVideoId,
          channelId: 'default',
          speakingRate: 1.10
        })
      });
      
      if (!response.ok) {
        throw new Error(`Ses oluşturma hatası: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('API yanıtı:', result);
      
      if (result.audioContent) {
        const byteCharacters = atob(result.audioContent);
        const byteNumbers = new Array(byteCharacters.length);
        
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'audio/mp3' });
        
        audioUrl.value = URL.createObjectURL(blob);
        
        // Audio elementlerini oluştur
        if (!ttsAudio.value) {
          ttsAudio.value = new Audio(audioUrl.value);
          ttsAudio.value.addEventListener('ended', handleTTSEnded);
          ttsAudio.value.addEventListener('timeupdate', updateAudioProgress);
        } else {
          ttsAudio.value.src = audioUrl.value;
        }
        
        if (!backgroundAudio.value) {
          backgroundAudio.value = new Audio('/audio/background.mp3');
          backgroundAudio.value.loop = true;
        }
        
        console.log('✅ Yeni TTS başarıyla oluşturuldu');
        return true;
      } else {
        throw new Error('Ses içeriği alınamadı');
      }
    } catch (error) {
      console.error(`TTS denemesi ${retryCount + 1} başarısız:`, error);
      
      if (error instanceof Error && error.message.includes('ENETDOWN')) {
        // Ağ hatası durumunda yeniden dene
        if (retryCount < maxRetries - 1) {
          retryCount++;
          await delay(2000 * retryCount);
          return attemptTTS(language);
        }
      }
      throw error;
    }
  };
  
  try {
    await attemptTTS(videoLanguage);
  } catch (finalError) {
    console.error('Tüm TTS denemeleri başarısız:', finalError);
    error.value = languageStore.currentLocale === 'tr' ? 
      'Ses oluşturma sırasında bir hata oluştu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.' : 
      'An error occurred while generating audio. Please check your internet connection and try again.';
  } finally {
    isLoadingAudio.value = false;
  }
};
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Auth Section - artık App.vue'da olduğu için burayı kaldırıyoruz -->
    
    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
      <!-- Banner Section -->
      <div class="text-center max-w-4xl mx-auto pb-12">
        <h1 class="text-4xl sm:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 leading-tight">
          {{ languageStore.t('home.banner.title') }}
        </h1>
        <h2 class="text-xl sm:text-2xl text-gray-600 leading-relaxed font-medium mt-4 max-w-3xl mx-auto">
          {{ languageStore.t('home.banner.subtitle') }}
        </h2>
      </div>

      <!-- Search Section -->
      <div class="relative max-w-3xl mx-auto mb-12">
        <!-- Gradient Background -->
        <div class="absolute inset-0 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl"></div>
        
        <!-- Search Form -->
        <div class="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-100">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="languageStore.t('home.search.placeholder')"
              @keyup.enter="handleSearch"
              class="w-full px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg rounded-xl border-2 border-gray-100 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300 shadow-sm hover:border-gray-200 bg-white/80 backdrop-blur-sm"
              :class="{ 'pr-24 sm:pr-36': !isLoading }"
            />
            <button
              @click="handleSearch"
              class="absolute right-2 top-1/2 -translate-y-1/2 px-3 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 font-medium text-xs sm:text-sm shadow-lg hover:shadow-xl group"
            >
              <span class="flex items-center gap-2">
                {{ languageStore.t('home.search.button') }}
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </button>
          </div>

          <!-- Decorative Elements -->
          <div class="absolute -top-6 -left-6 w-12 h-12 bg-indigo-100 rounded-full animate-pulse"></div>
          <div class="absolute -bottom-4 -right-4 w-8 h-8 bg-purple-100 rounded-full animate-pulse delay-150"></div>
          <div class="absolute top-1/2 -right-8 w-4 h-4 bg-pink-100 rounded-full animate-pulse delay-300"></div>
        </div>
      </div>

      <!-- Results Section -->
      <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-8">
        <!-- Add error message at the top if exists -->
        <div v-if="error" class="mb-4 p-4 bg-red-100 text-red-800 rounded-xl mx-4 mt-4">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>{{ error }}</span>
          </div>
        </div>
        
        <!-- Add processing status indicator -->


        <div class="grid grid-cols-1 lg:grid-cols-12 gap-4 p-3 sm:p-4 lg:p-6">
          <!-- Video Section -->
          <div class="lg:col-span-6">
            <div class="relative w-full bg-black rounded-xl overflow-hidden shadow-lg" style="padding-top: 56.25%;">
              <iframe
                :src="`https://www.youtube.com/embed/${videoId}`"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="absolute top-0 left-0 w-full h-full"
              ></iframe>
            </div>
            <div class="mt-3">
              <div class="grid grid-cols-1 gap-2">
                <template v-if="isLoadingRecentSummaries">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div v-for="n in 4" :key="n" class="animate-pulse">
                      <div class="aspect-video bg-gray-200 rounded-lg"></div>
                      <div class="mt-2 h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <!-- Debug information -->
                  <div v-if="recentSummaries.length === 0" class="p-4 mb-4 text-center bg-yellow-50 rounded-lg">
                    <p class="text-yellow-700">{{ languageStore.t('home.summary.noSummaries') }}</p>
                  </div>
                  
                  <Carousel
                    :items-to-show="1.5"
                    :breakpoints="{
                      480: { itemsToShow: 1.8 },
                      640: { itemsToShow: 2.2 },
                      768: { itemsToShow: 3 },
                      1024: { itemsToShow: 4 }
                    }"
                    :wrap-around="true"
                    :transition="500"
                    class="recent-summaries-carousel -mt-1"
                  >
                    <Slide v-for="summary in recentSummaries" :key="summary.id">
                      <div class="carousel__item bg-white rounded-lg shadow-md overflow-hidden mx-1 my-1">
                        <div class="relative aspect-video overflow-hidden cursor-pointer" @click="handleThumbnailClick(summary.video_id, summary.language)">
                          <img 
                            :src="summary.videos?.thumbnail_url || `https://img.youtube.com/vi/${summary.video_id}/maxresdefault.jpg`"
                            :alt="summary.videos?.title || 'Video thumbnail'"
                            class="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
                            loading="lazy"
                          />
                          <div class="absolute top-2 right-2 px-2 py-1 bg-blue-600/70 backdrop-blur-sm text-white text-xs font-bold rounded">
                            {{ summary.language?.toUpperCase() }}
                          </div>
                        </div>
                        <div class="p-2">
                          <p class="text-xs text-gray-500 truncate">
                            {{ summary.videos?.channel_title || summary.channel?.title || 'Kanal Adı' }}
                          </p>
                          <p class="text-xs text-gray-400 mt-1">
                            {{ formatPublishedDate(summary.created_at) }}
                          </p>
                        </div>
                      </div>
                    </Slide>
                    <template #addons>
                      <Navigation />
                    </template>
                  </Carousel>
                </template>
              </div>
            </div>
          </div>

          <!-- Content Section -->
          <div class="lg:col-span-6">
            <!-- Tab Controls -->
            <div class="flex overflow-x-auto mb-4 bg-gray-50 p-1 rounded-xl">
              <button
                v-for="tab in ['summary', 'transcript', 'listen']"
                :key="tab"
                @click="handleTabClick(tab)"
                class="flex-1 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200"
                :class="{ 'bg-white shadow-md text-indigo-600 scale-[1.02]': activeTab === tab, 'text-gray-600 hover:text-indigo-600 hover:bg-white/50': activeTab !== tab }"
              >
                <span class="flex items-center justify-center gap-2">
                  <svg v-if="tab === 'summary'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span class="hidden sm:inline">{{ languageStore.t(`home.tabs.${tab}`) }}</span>
                  <span class="sm:hidden">{{ languageStore.t(`home.tabs.${tab}Short`) }}</span>
                </span>
              </button>
            </div>

            <!-- Tab Content -->
            <div class="bg-gray-50 rounded-xl p-6">
              <!-- Summary Tab -->
              <div v-if="activeTab === 'summary'" class="h-full">
                <div v-if="isLoadingSummary" class="flex flex-col items-center justify-center space-y-6 py-12">
                  <!-- Animated Loading Spinner -->
                  <div class="relative">
                    <!-- Outer ring -->
                    <div class="w-16 h-16 border-4 border-indigo-100 rounded-full animate-pulse"></div>
                    <!-- Inner spinner -->
                    <div class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-indigo-600 rounded-full animate-spin"></div>
                    <!-- Center dot -->
                    <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-indigo-600 rounded-full"></div>
                  </div>
                  <!-- Loading Text -->
                  <div class="text-center space-y-2">
                    <div class="text-base font-medium text-gray-900">
                      {{ processingStatus.currentStep || languageStore.t('home.processing.creatingSummary') }}
                    </div>
                    <div class="flex items-center justify-center gap-1">
                      <div class="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce" style="animation-delay: 0s"></div>
                      <div class="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                      <div class="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce" style="animation-delay: 0.4s"></div>
                    </div>
                    <p class="text-sm text-gray-500">{{ languageStore.t('home.processing.timeEstimate') }}</p>
                  </div>
                </div>
                <div v-else-if="error" class="text-center py-8">
                  <p class="text-red-500">{{ error }}</p>
                  <button 
                    @click="retryProcessing" 
                    class="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
                  >
                    {{ languageStore.t('common.retry') }}
                  </button>
                </div>
                <div v-else>
                  <!-- Eğer videoData.summary varsa göster -->
                  <div v-if="videoData.summary" class="prose prose-indigo max-w-none">
                    <!-- Video başlığını ekliyorum -->
                    <h2 v-if="videoData.title && videoData.id === videoId" class="text-lg font-bold text-gray-800 mb-2">
                      {{ videoData.title }}
                    </h2>
                    <div class="content-paragraph text-gray-700 leading-relaxed whitespace-pre-line" v-html="formattedSummaryPreview"></div>
                    <span v-if="videoData.summary.length > 300">...</span>
                    <button
                      @click="handleSummaryClick(videoData)"
                      class="mt-4 inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
                    >
                      {{ languageStore.t('home.summary.detailButton') }}
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  <!-- Hiç özet yoksa -->
                  <div v-else class="text-center py-8">
                    <p class="text-gray-500">{{ languageStore.t('home.summary.noSummaries') }}</p>
                  </div>
                </div>
              </div>

              <!-- Transcript Tab -->
              <div v-else-if="activeTab === 'transcript'" class="h-full">
                <div v-if="isLoadingTranscript" class="flex flex-col items-center justify-center space-y-6 py-12">
                  <!-- Animated Loading Spinner -->
                  <div class="relative">
                    <!-- Outer ring -->
                    <div class="w-16 h-16 border-4 border-purple-100 rounded-full animate-pulse"></div>
                    <!-- Inner spinner -->
                    <div class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-purple-600 rounded-full animate-spin"></div>
                    <!-- Center dot -->
                    <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-purple-600 rounded-full"></div>
                  </div>
                  <!-- Loading Text -->
                  <div class="text-center space-y-2">
                    <div class="text-base font-medium text-gray-900">
                      {{ processingStatus.currentStep || languageStore.t('home.processing.creatingTranscript') }}
                    </div>
                    <div class="flex items-center justify-center gap-1">
                      <div class="w-1.5 h-1.5 bg-purple-600 rounded-full animate-bounce" style="animation-delay: 0s"></div>
                      <div class="w-1.5 h-1.5 bg-purple-600 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                      <div class="w-1.5 h-1.5 bg-purple-600 rounded-full animate-bounce" style="animation-delay: 0.4s"></div>
                    </div>
                    <p class="text-sm text-gray-500">{{ languageStore.t('home.processing.timeEstimate') }}</p>
                  </div>
                </div>
                <div v-else-if="error" class="text-center py-8">
                  <p class="text-red-500">{{ error }}</p>
                  <button 
                    @click="retryProcessing" 
                    class="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
                  >
                    {{ languageStore.t('common.retry') }}
                  </button>
                </div>
                <div v-else>
                  <div v-if="videoData.transcriptPreview" class="prose prose-indigo max-w-none">
                    <!-- Video başlığını ekliyorum -->
                    <h2 v-if="videoData.title && videoData.id === videoId" class="text-lg font-bold text-gray-800 mb-2">
                      {{ videoData.title }}
                    </h2>
                    <p class="text-gray-700 leading-relaxed whitespace-pre-line">
                      {{ videoData.transcriptPreview }}
                      <span v-if="videoData.transcript && videoData.transcript.length > 400" class="text-gray-500">...</span>
                    </p>
                    <button 
                      @click="showTranscriptModal = true"
                      class="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
                    >
                      {{ languageStore.t('home.transcript.detailButton') }}
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  <div v-else class="text-center py-8">
                    <p class="text-gray-500">{{ languageStore.t('home.transcript.noTranscript') }}</p>
                  </div>
                </div>
              </div>

              <!-- Listen Tab -->
              <div v-else-if="activeTab === 'listen'" class="h-full">
                <div v-if="!videoData.summary" class="text-center py-8">
                  <p class="text-gray-500">{{ languageStore.t('home.listen.noSummary') }}</p>
                </div>
                <div v-else>
                  <div class="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 relative overflow-hidden">
                    <div class="absolute inset-0 bg-grid-gray-100 opacity-[0.05]"></div>
                    <div class="relative">
                      <div class="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-full flex items-center justify-center group">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-indigo-500 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072M12 18.364a7 7 0 010-12.728M8.464 15.536a5 5 0 010-7.072m12.02 4.072a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p class="text-gray-600 mb-6">{{ languageStore.t('home.listen.description') }}</p>
                      <div v-if="isLoadingAudio" class="flex flex-col items-center justify-center mb-4">
                        <div class="spinner animate-spin h-8 w-8 border-4 border-indigo-500 rounded-full border-t-transparent mb-2"></div>
                        <p class="text-sm text-gray-500">{{ languageStore.t('home.listen.loading') }}</p>
                      </div>
                      
                      <!-- Ses Kontrolleri -->
                      <div v-if="ttsAudio" class="space-y-4">
                        <!-- Oynatma/Durdurma Butonları -->
                        <div class="flex justify-center gap-4">
                          <button 
                            v-if="!isPlaying"
                            @click="playAudio"
                            class="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
                            </svg>
                            <span>{{ ttsAudio?.currentTime === 0 ? languageStore.t('home.listen.playButton') : languageStore.t('home.listen.playAgainButton') }}</span>
                          </button>
                          <button 
                            v-else
                            @click="stopAudio"
                            class="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1zm4 0a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                            <span>{{ languageStore.t('home.listen.stopButton') }}</span>
                          </button>
                        </div>
                        
                        <!-- İlerleme Çubuğu ve Süre -->
                        <div class="space-y-2">
                          <div class="flex items-center justify-between text-sm text-gray-600 mb-1">
                            <span>{{ currentTime }}</span>
                            <span>{{ totalDuration }}</span>
                          </div>
                          <div class="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              class="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                              :style="{ width: audioProgress + '%' }"
                            ></div>
                          </div>
                        </div>
                        
                        <!-- Ses Seviyeleri -->
                        <div class="space-y-2 hidden sm:block">
                          <div class="flex items-center gap-2">
                            <span class="text-sm text-gray-600">{{ languageStore.t('home.listen.ttsVolume') }}:</span>
                            <input 
                              type="range" 
                              v-model="ttsVolume" 
                              min="0" 
                              max="1" 
                              step="0.1"
                              class="w-full"
                            />
                          </div>
                          <div class="flex items-center gap-2">
                            <span class="text-sm text-gray-600">{{ languageStore.t('home.listen.bgVolume') }}:</span>
                            <input 
                              type="range" 
                              v-model="bgVolume" 
                              min="0" 
                              max="1" 
                              step="0.1"
                              class="w-full"
                            />
                          </div>
                        </div>
                      </div>

                                          <button 
                      v-if="!ttsAudio && !audioUrl"
                      @click="textToSpeech" 
                      :disabled="isLoadingAudio || !videoData.summary"
                      class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg transition-colors"
                      :class="isLoadingAudio || !videoData.summary ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'"
                    >
                      <span>{{ languageStore.t('home.listen.generateButton') }}</span>
                    </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Features Section -->
      <FeatureSection :key="`feature-${currentLanguage}-${forceRender}`" />

      <!-- Popular Videos Section -->
      <PopularVideos :key="`popular-videos-${currentLanguage}-${forceRender}`" />

      <!-- Testimonials Section -->
      <TestimonialSection :key="`testimonial-${currentLanguage}-${forceRender}`" />

      <!-- How It Works Section -->
      <HowItWorksSection :key="`howworks-${currentLanguage}-${forceRender}`" />

      <!-- Modals -->
      <DetailModal 
        :show-modal="showDetailModal"
        :video-data="videoData"
        :video-id="videoId"
        :formatted-summary="formattedSummary"
        @close="showDetailModal = false"
        @share-summary="shareSummary"
        @download-summary="downloadSummary"
      />

      <TranscriptModal 
        :show-modal="showTranscriptModal"
        :video-data="videoData"
        :video-id="videoId"
        @close="showTranscriptModal = false"
        @share-transcript="shareTranscript"
        @download-transcript="downloadTranscript"
      />

      <LanguageModal 
        :show-modal="showLanguageModal"
        :selected-language="selectedLanguage"
        :videoModeOnly="true"
        @select-language="processVideoWithLanguage"
        @close="showLanguageModal = false"
      />

      <!-- Add summary list -->
      <div v-if="summaries?.length" class="grid grid-cols-1 gap-4">
        <div v-for="summary in summaries" 
             :key="summary.id"
             class="flex justify-between items-center hover:bg-gray-50 p-4 rounded-lg">
          <h3 class="font-medium cursor-pointer" @click="handleSummaryClick(summary)">{{ summary.videoTitle }}</h3>
          <button @click="closeSummary" class="text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- DEV MODE DEBUGGING CONTROLS - only shown in development -->
      <div v-if="isDev" class="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
        <div class="flex items-center justify-between">
          <h3 class="text-amber-700 font-semibold text-sm">Development Mode Debug Controls</h3>
          <button @click="showDebug = !showDebug" class="text-amber-700 hover:text-amber-900">
            {{ showDebug ? 'Hide Controls' : 'Show Controls' }}
          </button>
        </div>
        <div v-if="showDebug" class="mt-3 space-y-3">
          <p class="text-amber-600 text-xs">These controls are only available in development mode</p>
          <div class="flex flex-wrap gap-2">
            <button 
              @click="emergencyStopAllPolling" 
              class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-sm rounded"
            >
              🛑 Emergency Stop All Polling
            </button>
            <button 
              @click="pollingService.stopAllPolling(videoData?.id)" 
              class="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 text-sm rounded"
              v-if="videoData?.id"
            >
              Stop Polling for {{ videoData?.id }}
            </button>
            <button 
              @click="clearAllIntervals" 
              class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 text-sm rounded"
            >
              Clear All Intervals
            </button>
          </div>
          <div class="text-xs text-amber-600 mt-2">
            <p>Current polling status: {{ pollingService.isLoadingTranscript.value || pollingService.isLoadingSummary.value ? 'ACTIVE' : 'INACTIVE' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Error State -->
  <div v-if="error" class="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
    <div class="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl text-center max-w-md mx-4">
      <div class="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-red-100 rounded-full">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div class="text-lg font-medium text-red-600 mb-2">Hata Oluştu</div>
      <p class="text-sm text-gray-500">{{ error }}</p>
      <button 
        @click="error = ''" 
        class="mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
      >
        Kapat
      </button>
    </div>
  </div>
</template>
<style scoped>
body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 20px;
  background-color: #f0f4ff;
}

.search-bar {
  width: 100%;
  margin: 20px auto;
  position: relative;
}

.search-bar input {
  width: 100%;
  padding: 12px 40px 12px 15px;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 16px;
}

.search-bar button {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  cursor: pointer;
  font-size: 20px;
}

.video-container {
  background: white;
  border-radius: 15px;
  padding: 20px;
  margin: 0 auto;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.controls {
  display: flex;
  gap: 15px;
  margin: 15px 0;
  background: #f8f9fa;
  padding: 8px;
  border-radius: 20px;
  width: fit-content;
}

.control-btn {
  padding: 8px 16px;
  border: none;
  background: white;
  font-size: 14px;
  cursor: pointer;
  border-radius: 15px;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.control-btn:hover {
  background: #f0f0f0;
  transform: translateY(-1px);
}

.content-section {
  margin-top: 15px;
  width: 100%;
}

.content-section h2 {
  font-size: 20px;
  margin-bottom: 15px;
}

.content-item {
  display: flex;
  align-items: start;
  gap: 10px;
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 8px;
  background: #f8f9fa;
}

.content-item img {
  width: 24px;
  height: 24px;
}

.content-item p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

.content-paragraph {
  font-size: 1rem;
  line-height: 1.6;
  color: #4a5568;
  margin-bottom: 1rem;
}

.content-paragraph :deep(.highlight-concept) {
  background: linear-gradient(120deg, rgba(255, 242, 204, 0.8) 0%, rgba(255, 236, 179, 0.8) 100%);
  padding: 0.1em 0.3em;
  border-radius: 3px;
  font-weight: 500;
}

.content-paragraph :deep(.highlight-quote) {
  background: linear-gradient(120deg, rgba(232, 245, 233, 0.8) 0%, rgba(200, 230, 201, 0.8) 100%);
  padding: 0.1em 0.3em;
  border-radius: 3px;
  font-style: italic;
}

.content-paragraph :deep(.highlight-stat) {
  background: linear-gradient(120deg, rgba(227, 242, 253, 0.8) 0%, rgba(187, 222, 251, 0.8) 100%);
  padding: 0.1em 0.3em;
  border-radius: 3px;
  font-weight: 500;
}

.aspect-video {
  aspect-ratio: 16/9;
  width: 100%;
}

.recent-summaries-carousel {
  padding: 20px 40px;
  position: relative;
  margin: 0 -20px;
}

.carousel__item {
  background-color: white !important;
  height: auto !important;
  padding-bottom: 0 !important;
  margin: 0 8px !important;
}

.carousel__slide {
  padding: 5px;
}

.carousel__prev,
.carousel__next {
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

.carousel__prev:hover,
.carousel__next:hover {
  background-color: rgba(0, 0, 0, 0.7);
  transform: translateY(-50%) scale(1.1);
}

.carousel__prev {
  left: 4px;
}

.carousel__next {
  right: 4px;
}

.recent-summaries-carousel :deep(.carousel__viewport) {
  padding: 0;
}

.recent-summaries-carousel :deep(.carousel__pagination) {
  margin-top: 0;
}

@media (max-width: 640px) {
  .recent-summaries-carousel {
    padding: 20px 32px;
    margin: 0 -10px;
  }
  
  .carousel__prev,
  .carousel__next {
    width: 32px;
    height: 32px;
  }
  
  .carousel__prev {
    left: 2px;
  }
  
  .carousel__next {
    right: 2px;
  }

  .carousel__item {
    margin: 0 4px !important;
  }

  .recent-summaries-carousel .carousel__slide {
    max-width: 85%; /* Mobilde daha küçük genişlik */
    padding: 3px;
  }
  
  .recent-summaries-carousel .carousel__item {
    transform: scale(0.9); /* Mobilde biraz daha küçük göster */
  }
}
</style>
